import type { QueryKey } from '@tanstack/react-query';
import type { QueryGroup } from '../../api';
import type {
  AsyncOperationFilters,
  OperationReference,
  OperationScope,
} from './asyncOperations.types';
import { getOperationScopeKey } from './asyncOperations.utils';

export const ASYNC_OPERATION_QUERY_GROUP = 'operations' satisfies QueryGroup;
export const ASYNC_OPERATION_DETAIL_QUERY_KEY = 'operation-detail';
export const ASYNC_OPERATIONS_LIST_QUERY_KEY = 'operations-list';

const getScopeQueryOptions = (scope: OperationScope) =>
  ({
    apiTarget: scope.placement.apiTarget,
    hideNotification: true,
    regionalApiBaseUrl:
      scope.placement.apiTarget === 'regional'
        ? scope.placement.regionalApiBaseUrl
        : undefined,
    teamScoped: false,
  }) as const;

const getScopeVariables = (scope: OperationScope): OperationScope => ({
  placement:
    scope.placement.apiTarget === 'regional'
      ? {
          apiTarget: scope.placement.apiTarget,
          dataRegion: scope.placement.dataRegion,
          regionalApiBaseUrl: scope.placement.regionalApiBaseUrl,
        }
      : {
          apiTarget: scope.placement.apiTarget,
          dataRegion: scope.placement.dataRegion,
        },
  requesterTeamId: scope.requesterTeamId,
});

export const asyncOperationQueries = {
  detail: (reference: OperationReference) => {
    const scope = getScopeVariables(reference);

    return {
      ...getScopeQueryOptions(reference),
      queryGroup: ASYNC_OPERATION_QUERY_GROUP,
      uniqueQueryKey: ASYNC_OPERATION_DETAIL_QUERY_KEY,
      sentryQueryKey: ASYNC_OPERATION_DETAIL_QUERY_KEY,
      variables: { ...scope, operationId: reference.operationId },
    } as const;
  },
  list: (scope: OperationScope, filters: AsyncOperationFilters) => {
    const variablesScope = getScopeVariables(scope);

    return {
      ...getScopeQueryOptions(scope),
      queryGroup: ASYNC_OPERATION_QUERY_GROUP,
      uniqueQueryKey: ASYNC_OPERATIONS_LIST_QUERY_KEY,
      variables: { filters, scope: variablesScope },
    } as const;
  },
};

export const isAsyncOperationsListQueryKey = (
  queryKey: QueryKey,
  scope?: OperationScope
) => {
  if (
    queryKey[0] !== ASYNC_OPERATION_QUERY_GROUP ||
    queryKey[1] !== ASYNC_OPERATIONS_LIST_QUERY_KEY
  ) {
    return false;
  }

  if (!scope) return true;

  const variables = queryKey[3];
  if (!variables || typeof variables !== 'object' || !('scope' in variables)) {
    return false;
  }

  return (
    getOperationScopeKey(variables.scope as OperationScope) ===
    getOperationScopeKey(scope)
  );
};
